import { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle2, Loader2 } from 'lucide-react';
import { ope } from '../../../services/api';
import { useToast } from '../../../context/ToastContext';

const OpeAdminSummaryGenerator = ({ temaId, tieneResumen, onSummaryGenerated }) => {
    const [promptResumen, setPromptResumen] = useState('');
    const [generatingSummary, setGeneratingSummary] = useState(false);
    const [resumenResult, setResumenResult] = useState(null);
    const { success, error } = useToast();

    useEffect(() => {
        if (!temaId) return;
        loadPromptResumen();
        loadResumen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [temaId]);

    const loadPromptResumen = async () => {
        try {
            const data = await ope.adminGetPromptResumen(temaId);
            setPromptResumen(data.prompt);
        } catch (err) {
            error(err.message || 'Error al cargar prompt de resumen');
        }
    };

    const loadResumen = async () => {
        try {
            const data = await ope.adminGetResumen(temaId);
            if (data.resumen) {
                setResumenResult({ resumen: data.resumen, generadoAt: data.generadoAt });
            }
        } catch (err) {
            console.error('Error loading resumen:', err);
        }
    };

    const handleGenerateResumen = async () => {
        try {
            setGeneratingSummary(true);
            const result = await ope.adminGenerateSummary(temaId, promptResumen);
            console.log('Resumen generado (backend result):', result);

            setResumenResult(result);
            success(`Resumen generado: ${result.chars.toLocaleString()} caracteres (${result.tokensUsados} tokens)`);

            if (onSummaryGenerated) onSummaryGenerated(result);

        } catch (err) {
            console.error('Error generando resumen:', err);
            error(`Error: ${err.message}`);
        } finally {
            setGeneratingSummary(false);
        }
    };

    return (
        <div className="p-4 bg-white/5 rounded-xl border border-white/10 space-y-4 ml-4">
            <div>
                <label className="block text-sm text-gray-400 mb-2">
                    Prompt para GPT-4o <span className="text-indigo-400">(editable)</span>:
                </label>
                <textarea
                    value={promptResumen}
                    onChange={e => setPromptResumen(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-indigo-500 resize-y"
                    placeholder="Cargando prompt..."
                />
                <p className="text-xs text-gray-500 mt-1">
                    Usa <code className="text-indigo-400">{'{texto}'}</code> como placeholder para el contenido del PDF.
                </p>
            </div>

            <button
                onClick={handleGenerateResumen}
                disabled={generatingSummary || !promptResumen}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
            >
                {generatingSummary ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Generando con GPT-4o... (puede tardar 30-60s)</>
                ) : (
                    <><RefreshCw className="w-4 h-4" /> {tieneResumen ? 'Regenerar Resumen' : 'Generar Resumen'}</>
                )}
            </button>

            {resumenResult?.resumen && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-green-400">
                        <CheckCircle2 className="w-4 h-4" />
                        Resumen generado ({resumenResult.resumen.length.toLocaleString()} chars)
                        {resumenResult.tokensUsados && ` — ${resumenResult.tokensUsados} tokens`}
                    </div>
                    <div className="max-h-80 overflow-y-auto p-3 bg-black/30 rounded-lg text-sm text-gray-300 whitespace-pre-wrap">
                        {resumenResult.resumen}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OpeAdminSummaryGenerator;
